import type { AxiosDefaults, AxiosHeaderValue, AxiosInstance, HeadersDefaults } from "axios";
import type { Page } from "@zhihaoscm/entity";
import type { CustomAxiosRequestConfig } from "@/api";
import { confirmable } from "@/utils/decorator/confirmable";

/**
 * 基础请求服务
 * E entity类型
 * V Vo类型
 * P pagingConditions类型
 * F entityForm类型 或等同于entity类型
 */
export class BaseService<E, V, P, F> {
    protected readonly service: AxiosInstance;

    protected readonly path: string;

    constructor(path: string, service: AxiosInstance, config?: Partial<CustomAxiosRequestConfig>) {
        this.service = service;
        this.path = path;
        this.service.defaults.baseURL = `${this.service.defaults.baseURL}`;
        if (config)
            this.service.defaults = { ...this.service.defaults, ...config } as unknown as Omit<
                AxiosDefaults,
                "headers"
            > & {
                headers: HeadersDefaults & {
                    [key: string]: AxiosHeaderValue;
                };
            };
    }

    /**
     * 主页数据
     */
    public async paging(params: P): Promise<Page<E>> {
        const { data } = await this.service.get(`${this.path}/paging`, { params });
        return data;
    }

    /**
     * 下拉选框列表数据
     */
    public async selector(): Promise<Array<E>> {
        const { data } = await this.service.get(`${this.path}/selector`);
        return data;
    }

    /**
     * 根据ID查询
     * @param id
     */
    public async get(id: number | string): Promise<E> {
        const { data } = await this.service.get(`${this.path}/${id}`);
        return data;
    }

    /**
     * 根据ID查询
     * @param id
     */
    public async getInfo(id: number | string): Promise<V> {
        const { data } = await this.service.get(`${this.path}/${id}`);
        return data;
    }

    /**
     * 根据ID查询
     * @param id
     */
    public async getVo(id: number | string): Promise<V> {
        const { data } = await this.service.get(`${this.path}/vo/${id}`);
        return data;
    }

    /**
     * 创建实例
     * @param form
     */
    @confirmable("是否保存")
    public async create(form: F): Promise<E> {
        for (const key in form) {
            const value = form[key];
            if (value === undefined || value === null || value === "") {
                delete form[key];
            }
        }
        const { data } = await this.service.post(this.path, form);
        return data;
    }

    /**
     * 更新实例
     * @param id
     * @param form
     */
    @confirmable("是否保存")
    public async update(id: number | string, form: F) {
        const { data } = await this.service.put(`${this.path}/${id}`, form);
        return data;
    }

    /**
     * 删除实例
     * @param id
     */
    @confirmable("是否删除")
    public async delete(id: number | string) {
        const { data } = await this.service.delete(`${this.path}/${id}`);
        return data;
    }
}
